import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './ConversationalQuote.css';
import mapEventTypeToConversationalService from '../../utils/mapEventTypeToConversationalService';
import QuoteSummaryCard from '../../Components/QuoteSummaryCard/QuoteSummaryCard';
import buildQuoteSummaryPayload from '../../utils/buildQuoteSummaryPayload';
import { mergeQuoteRequirements } from '../../utils/mergeQuoteRequirements';

const SERVICES = {
  Wedding: [
    { id:'welcome', bot:"Thanks for filling out the form — we have your details. Just a few quick questions and our team will have your personalised quote ready.", autoNext:true },
    { id:'span', bot:"Is the celebration happening on one day or across multiple days?", chips:['Single day','Multi-day event'], key:'Event span' },
    { id:'dates', bot:"Please select all the dates your event spans across.", calendar:true, key:'Event dates', skipIf: a => a['Event span']==='Single day' },
    { id:'ceremonies', bot:"Which ceremonies would you like covered? Select each one and add the expected guest count.", withGuests:true, items:['Mehendi','Haldi','Sangeet','Wedding / Pheras','Reception','Engagement','Tilak / Roka','Pre-wedding shoot','Other'], key:'Ceremonies' },
    { id:'addons', bot:"Any add-ons you have in mind?", chips:['Drone shots','Wedding album','Instagram reels','Same-day edit','Live streaming','Not sure yet'], multi:true, key:'Add-ons' },
    { id:'notes', bot:"Anything specific you would like us to know — a vibe, a reference, or a must-capture moment?", freeText:true, optional:true, key:'Notes' },
    { id:'done', bot:"That is everything. Our team will be in touch within 2 hours with your personalised quote.", final:true }
  ],
  Events: [
    { id:'welcome', bot:"Thanks for reaching out. A few quick questions and we will have a quote ready for you.", autoNext:true },
    { id:'span', bot:"Is this a single day event or does it run across multiple days?", chips:['Single day','Multi-day'], key:'Event span' },
    { id:'dates', bot:"Please select all the dates your event runs across.", calendar:true, key:'Event dates', skipIf: a => a['Event span']==='Single day' },
    { id:'type', bot:"What type of event is this? Select all that apply and add the expected guest count.", withGuests:true, items:['Birthday / anniversary','Concert / performance','Award night','Brand launch','Exhibition / expo','Festival','Other'], key:'Event type' },
    { id:'setting', bot:"Will it be indoor, outdoor, or both?", chips:['Indoor','Outdoor','Both'], key:'Setting' },
    { id:'addons', bot:"Any add-ons you have in mind?", chips:['Drone shots','Instagram reels','Same-day edit','Live streaming','Event highlights video','Not sure yet'], multi:true, key:'Add-ons' },
    { id:'notes', bot:"Any specific moments or requirements you would like us to know about?", freeText:true, optional:true, key:'Notes' },
    { id:'done', bot:"That is everything. Our team will be in touch within 2 hours with your personalised quote.", final:true }
  ],
  Corporate: [
    { id:'welcome', bot:"Thanks for getting in touch. A few quick questions so our team can put together the right package for you.", autoNext:true },
    { id:'span', bot:"Will this be a single day or a multi-day engagement?", chips:['Single day','Multi-day'], key:'Event span' },
    { id:'dates', bot:"Please select all the dates.", calendar:true, key:'Event dates', skipIf: a => a['Event span']==='Single day' },
    { id:'type', bot:"What type of corporate event is this? Add the expected attendee count.", withGuests:true, items:['Conference / seminar','Product launch','Team offsite','Award ceremony','Brand shoot','AGM / townhall','Other'], key:'Event type' },
    { id:'stage', bot:"Will there be stage coverage — presentations, speeches, or panel discussions?", chips:['Yes','No','Not sure'], key:'Stage coverage' },
    { id:'addons', bot:"Any add-ons required?", chips:['Drone shots','Event highlights video','Same-day edit','LinkedIn / social media cuts','Live streaming','Not sure yet'], multi:true, key:'Add-ons' },
    { id:'notes', bot:"Any specific requirements or deliverables you have in mind?", freeText:true, optional:true, key:'Notes' },
    { id:'done', bot:"Thank you. Our team will review this and reach out within 2 hours with a tailored quote.", final:true }
  ],
  Fashion: [
    { id:'welcome', bot:"Great to hear from you. A couple of quick questions to help us tailor the right package.", autoNext:true },
    { id:'type', bot:"What type of fashion shoot is this?", chips:['Lookbook','Editorial','E-commerce / catalogue','Campaign','Reel / video content','Other'], key:'Shoot type' },
    { id:'looks', bot:"How many outfits or looks are planned?", chips:['1–3 looks','4–6 looks','7–10 looks','10+ looks'], key:'No. of looks' },
    { id:'setting', bot:"Will the shoot be in a studio, on location, or both?", chips:['Studio','Outdoor / on location','Both'], key:'Setting' },
    { id:'addons', bot:"Any add-ons you would like?", chips:['Behind the scenes video','Instagram reels','Same-day edit','Retouching','Not sure yet'], multi:true, key:'Add-ons' },
    { id:'notes', bot:"Any references, mood board, or specific direction you have in mind?", freeText:true, optional:true, key:'Notes' },
    { id:'done', bot:"Perfect. Our team will be in touch within 2 hours with your personalised quote.", final:true }
  ],
  Product: [
    { id:'welcome', bot:"Thanks for reaching out. A few quick questions so we can quote you accurately.", autoNext:true },
    { id:'category', bot:"What category of product are we shooting?", chips:['Apparel / fashion','Jewellery / accessories','Electronics / gadgets','Home décor / furniture','Beauty / skincare','Food & beverage','Other'], key:'Product category' },
    { id:'skus', bot:"How many products or SKUs need to be covered?", chips:['1–5','6–15','16–30','30–50','50+'], key:'No. of SKUs' },
    { id:'setting', bot:"Will this be a studio shoot, lifestyle shoot, or both?", chips:['Studio (white / clean background)','Lifestyle / contextual','Both'], key:'Setting' },
    { id:'addons', bot:"Any add-ons you would like?", chips:['360-degree spin video','Short product reels','Retouching','Packaging photography','Not sure yet'], multi:true, key:'Add-ons' },
    { id:'notes', bot:"Any specific style references or brand guidelines we should follow?", freeText:true, optional:true, key:'Notes' },
    { id:'done', bot:"Got it. Our team will review and be in touch within 2 hours with a quote.", final:true }
  ],
  Food: [
    { id:'welcome', bot:"Lovely to hear from you. Just a few quick questions to put together the right package.", autoNext:true },
    { id:'purpose', bot:"What is the shoot for?", chips:['Restaurant menu','Social media content','Packaging / label','Editorial / magazine','Brand campaign','Other'], key:'Purpose' },
    { id:'items', bot:"How many dishes or items need to be photographed?", chips:['1–5 items','6–15 items','16–30 items','30+'], key:'No. of items' },
    { id:'setting', bot:"Will this be at your location or in our studio?", chips:['At your restaurant / kitchen','At our studio','Both'], key:'Setting' },
    { id:'addons', bot:"Any add-ons you would like?", chips:['Recipe videos / reels','Behind the scenes','Prop sourcing','Food styling assistance','Not sure yet'], multi:true, key:'Add-ons' },
    { id:'notes', bot:"Any specific style references or brand feel you have in mind?", freeText:true, optional:true, key:'Notes' },
    { id:'done', bot:"Perfect. Our team will be in touch within 2 hours with your personalised quote.", final:true }
  ],
  Automobile: [
    { id:'welcome', bot:"Thanks for getting in touch. A few quick questions to help us plan the right shoot for you.", autoNext:true },
    { id:'purpose', bot:"What is this shoot for?", chips:['Showroom / dealership','Social media','Editorial / magazine','Ad campaign','Personal portfolio','Other'], key:'Purpose' },
    { id:'vehicles', bot:"How many vehicles need to be covered?", chips:['1 vehicle','2–3 vehicles','4–5 vehicles','6+'], key:'No. of vehicles' },
    { id:'setting', bot:"Will it be a studio shoot, outdoor, or both?", chips:['Studio','Outdoor / on location','Both'], key:'Setting' },
    { id:'addons', bot:"Any add-ons you would like?", chips:['360-degree walkaround video','Cinematic reel','Drone aerial shots','Detail / close-up shots','Not sure yet'], multi:true, key:'Add-ons' },
    { id:'notes', bot:"Any specific references or requirements for the shoot?", freeText:true, optional:true, key:'Notes' },
    { id:'done', bot:"Our team will review this and be in touch within 2 hours with a tailored quote.", final:true }
  ],
  'Maternity & Baby': [
    { id:'welcome', bot:"What a special time — we are glad you reached out. Just a couple of quick questions.", autoNext:true },
    { id:'type', bot:"What type of shoot are you looking for?", chips:['Maternity shoot','Newborn shoot','Cake smash / first birthday','Family portrait','Maternity + newborn combo'], key:'Shoot type' },
    { id:'setting', bot:"Would you prefer a studio session or at your home?", chips:['Studio','At home','Outdoor'], key:'Setting' },
    { id:'people', bot:"Who will be part of the shoot?", chips:['Mother only','Both parents','With siblings','Full family'], key:'People included' },
    { id:'addons', bot:"Any add-ons you would like?", chips:['Printed album','Canvas / framed prints','Instagram gallery set','Short video / reel','Not sure yet'], multi:true, key:'Add-ons' },
    { id:'notes', bot:"Any specific theme, colours, or props you have in mind?", freeText:true, optional:true, key:'Notes' },
    { id:'done', bot:"Perfect. Our team will be in touch within 2 hours with your personalised quote.", final:true }
  ],
  Sports: [
    { id:'welcome', bot:"Thanks for reaching out. A few quick questions to put together the right package for you.", autoNext:true },
    { id:'sport', bot:"What sport or activity is this for?", chips:['Cricket','Football / futsal','Basketball','Marathon / running','Gym / fitness','Yoga / wellness','Other'], key:'Sport / activity' },
    { id:'span', bot:"Is this a single session or across multiple days?", chips:['Single session','Multi-day'], key:'Event span' },
    { id:'dates', bot:"Please select all the dates.", calendar:true, key:'Event dates', skipIf: a => a['Event span']==='Single session' },
    { id:'style', bot:"What kind of coverage are you looking for?", chips:['Action shots','Portrait / headshots','Both action and portrait','Highlights video','Not sure yet'], key:'Coverage style' },
    { id:'setting', bot:"Will it be indoor or outdoor?", chips:['Indoor','Outdoor','Both'], key:'Setting' },
    { id:'addons', bot:"Any add-ons you would like?", chips:['Drone shots','Highlights reel','Same-day edit','Social media cuts','Not sure yet'], multi:true, key:'Add-ons' },
    { id:'notes', bot:"Anything specific you would like us to know?", freeText:true, optional:true, key:'Notes' },
    { id:'done', bot:"Our team will be in touch within 2 hours with your personalised quote.", final:true }
  ]
};

const SVC_ICONS = {
  'Wedding': '💍', 'Events': '🎉', 'Corporate': '🏢',
  'Fashion': '👗', 'Product': '📦', 'Food': '🍽️',
  'Automobile': '🚗', 'Maternity & Baby': '🤱', 'Sports': '🏅'
};

const ConversationalQuote = ({ onComplete, onSubmit, initialService, eventTypeLabel, quoteFormData }) => {
  const resolvedInitialService =
    (initialService && SERVICES[initialService]
      ? initialService
      : mapEventTypeToConversationalService(initialService || eventTypeLabel)) || null;

  const [activeService, setActiveService] = useState(resolvedInitialService);
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [multiSel, setMultiSel] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [isTyping, setIsTyping] = useState(false);
  const [inputValue, setInputValue] = useState("");
  
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calSelected, setCalSelected] = useState([]);
  
  const [guestSelections, setGuestSelections] = useState({});
  
  const msgsEndRef = useRef(null);
  const processedStepRef = useRef(null);

  const { steps, matchedService } = useMemo(() => {
    if (!activeService) return { steps: [], matchedService: null };
    const matched = Object.keys(SERVICES).find(
      (k) => k.toLowerCase() === activeService.toLowerCase()
    );
    return {
      matchedService: matched ?? null,
      steps: matched ? SERVICES[matched] : [],
    };
  }, [activeService]);

  const totalSteps = steps.length > 0 ? steps.length - 1 : 1;
  const currentStepObj = steps[currentStep];

  const displayFormData = React.useMemo(() => {
    if (!quoteFormData) return null;
    const ceremonies = answers.Ceremonies || quoteFormData.ceremonies;
    const addons = answers["Add-ons"] || quoteFormData.addons;
    return {
      ...quoteFormData,
      date: answers["Event dates"] || quoteFormData.date,
      eventSpan: answers["Event span"] || quoteFormData.eventSpan,
      ceremonies,
      addons,
      coverage: mergeQuoteRequirements({
        coverage: quoteFormData.coverage,
        ceremonies,
        addons,
      }),
    };
  }, [quoteFormData, answers]);

  useEffect(() => {
    if (msgsEndRef.current) {
      msgsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [msgs, isTyping]);

  const processStep = useCallback(() => {
    const step = steps[currentStep];
    if (!step) return;
    const stepProcessKey = `${activeService || "none"}-${currentStep}`;
    if (processedStepRef.current === stepProcessKey) return;
    processedStepRef.current = stepProcessKey;

    if (step.skipIf && step.skipIf(answers)) {
      setCurrentStep(prev => prev + 1);
      return;
    }

    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMsgs(prev => [...prev, { text: step.bot, isBot: true }]);
      
      if (step.autoNext) {
        setTimeout(() => setCurrentStep(prev => prev + 1), 400);
      }
    }, Math.min(step.bot.length * 15 + 300, 1300));
  }, [steps, currentStep, answers, activeService]);

  useEffect(() => {
    if (!activeService) return;
    processStep();
  }, [currentStep, activeService, processStep]);

  const submitAnswer = (answer, stepKey) => {
    processedStepRef.current = null;
    setMsgs(prev => [...prev, { text: answer, isBot: false }]);
    if (stepKey) {
      setAnswers(prev => ({ ...prev, [stepKey]: answer }));
    }
    setMultiSel([]);
    setGuestSelections({});
    setCurrentStep(prev => prev + 1);
  };

  const loadService = (name) => {
    processedStepRef.current = null;
    setActiveService(name);
    setCurrentStep(0);
    setAnswers({});
    setMultiSel([]);
    setMsgs([]);
    setInputValue("");
    setGuestSelections({});
  };

  const goBack = () => {
    processedStepRef.current = null;
    if (resolvedInitialService) {
      if (onComplete) onComplete();
    } else {
      setActiveService(null);
    }
  };

  const renderChips = () => {
    if (!currentStepObj || isTyping) return null;
    
    if (currentStepObj.withGuests) {
      const guestOptions = ['Under 50','50–150','150–300','300–500','500+'];
      const handleGuestSelect = (item, opt) => {
        setGuestSelections(prev => ({ ...prev, [item]: opt }));
      };
      
      const toggleCard = (item) => {
        setGuestSelections(prev => {
          if (prev.hasOwnProperty(item)) {
            const copy = { ...prev };
            delete copy[item];
            return copy;
          }
          return { ...prev, [item]: '' };
        });
      };

      const validSelections = Object.entries(guestSelections).filter(([,v]) => v);
      const keysLength = Object.keys(guestSelections).length;
      const allPicked = keysLength > 0 && validSelections.length === keysLength;

      const handleDone = () => {
        if (!allPicked) return;
        const summary = validSelections.map(([k,v]) => `${k} (${v})`).join(', ');
        const newAnswers = { ...answers };
        validSelections.forEach(([k,v]) => { newAnswers[`${currentStepObj.key} — ${k}`] = v; });
        newAnswers[currentStepObj.key] = validSelections.map(([k]) => k).join(', ');
        setAnswers(newAnswers);
        setMsgs(prev => [...prev, { text: summary, isBot: false }]);
        setCurrentStep(prev => prev + 1);
      };

      return (
        <>
          <div className="cq-wg-grid">
            {currentStepObj.items.map(name => {
              const isSel = guestSelections.hasOwnProperty(name);
              return (
                <div key={name} className={`cq-wg-card ${isSel ? 'sel' : ''}`} onClick={() => toggleCard(name)}>
                  <div className="cq-wg-name">{name}</div>
                  <div className="cq-wg-chips">
                    {guestOptions.map(opt => (
                      <div 
                        key={opt} 
                        className={`cq-wg-g ${guestSelections[name] === opt ? 'picked' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleGuestSelect(name, opt); }}
                      >
                        {opt}
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
          <div className="cq-wg-footer">
            <div className="cq-wg-hint">
              {keysLength === 0 ? 'Tap a ceremony to select, then pick guest count' : 
                allPicked ? `${validSelections.length} selected — ready to continue` : 'Pick guest count for all selected ceremonies'}
            </div>
            <button className="cq-chip-done" disabled={!allPicked} style={{ opacity: allPicked ? 1 : 0.35, cursor: allPicked ? 'pointer' : 'default' }} onClick={handleDone}>
              Done ✓
            </button>
          </div>
        </>
      );
    }

    if (currentStepObj.chips) {
      const handleChip = (label) => {
        if (currentStepObj.multi) {
          setMultiSel(prev => prev.includes(label) ? prev.filter(x => x !== label) : [...prev, label]);
        } else {
          submitAnswer(label, currentStepObj.key);
        }
      };

      return (
        <>
          {currentStepObj.chips.map(label => (
            <button key={label} className={`cq-chip ${currentStepObj.multi && multiSel.includes(label) ? 'sel' : ''}`} onClick={() => handleChip(label)}>
              {label}
            </button>
          ))}
          {currentStepObj.multi && (
            <button className="cq-chip-done" onClick={() => multiSel.length > 0 && submitAnswer(multiSel.join(', '), currentStepObj.key)}>
              Done ✓
            </button>
          )}
        </>
      );
    }

    if (currentStepObj.calendar) {
      const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
      const firstDay = new Date(calYear,calMonth,1).getDay();
      const daysInMonth = new Date(calYear,calMonth+1,0).getDate();
      const today = new Date(); today.setHours(0,0,0,0);
      
      const prevMonth = () => { let m = calMonth - 1; let y = calYear; if(m<0){m=11;y--;} setCalMonth(m); setCalYear(y); };
      const nextMonth = () => { let m = calMonth + 1; let y = calYear; if(m>11){m=0;y++;} setCalMonth(m); setCalYear(y); };
      
      let blanks = [];
      for(let i=0; i<firstDay; i++) blanks.push(<div key={`b-${i}`} className="cq-cal-day empty" />);
      
      let days = [];
      for(let d=1; d<=daysInMonth; d++) {
        const date = new Date(calYear,calMonth,d);
        const key = `${calYear}-${String(calMonth+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        const isPast = date <= today;
        const isSel = calSelected.includes(key);
        
        const onClick = () => {
          if(isPast) return;
          if(isSel) setCalSelected(prev => prev.filter(x => x !== key));
          else setCalSelected(prev => [...prev, key]);
        };
        
        days.push(
          <div key={d} className={`cq-cal-day ${isPast ? 'past' : ''} ${isSel ? 'sel' : ''}`} onClick={onClick}>
            {d}
          </div>
        );
      }
      
      const selNames = [...calSelected].sort().map(d=>{const[y,m,day]=d.split('-');return`${parseInt(day)} ${new Date(y,m-1,day).toLocaleString('default',{month:'short'})}`;});

      return (
        <div className="cq-cal-wrap">
          <div className="cq-cal-nav">
            <button className="cq-cal-nav-btn" onClick={prevMonth}>&#8249;</button>
            <div className="cq-cal-month">{months[calMonth]} {calYear}</div>
            <button className="cq-cal-nav-btn" onClick={nextMonth}>&#8250;</button>
          </div>
          <div className="cq-cal-grid">
            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d=><div key={d} className="cq-cal-day-label">{d}</div>)}
            {blanks}
            {days}
          </div>
          <div className="cq-cal-confirm">
            <div className="cq-cal-selected-label">
              {calSelected.length === 0 ? 'No dates selected' : <span>{selNames.join(', ')}</span>}
            </div>
            <button
              className="cq-cal-done-btn"
              disabled={calSelected.length === 0}
              onClick={() => {
                const sortedIsoDates = [...calSelected].sort();
                const displayDates = sortedIsoDates
                  .map((d) => {
                    const [y, m, day] = d.split("-");
                    return `${parseInt(day)} ${new Date(y, m - 1, day).toLocaleString("default", { month: "short" })}`;
                  })
                  .join(", ");

                processedStepRef.current = null;
                setMsgs((prev) => [...prev, { text: displayDates, isBot: false }]);
                setAnswers((prev) => ({
                  ...prev,
                  [currentStepObj.key]: displayDates,
                  [`${currentStepObj.key} ISO`]: sortedIsoDates.join(","),
                }));
                setCurrentStep((prev) => prev + 1);
              }}
            >
              Confirm dates
            </button>
          </div>
        </div>
      );
    }

    if (currentStepObj.optional && currentStepObj.freeText) {
      return (
        <button className="cq-chip-skip" onClick={() => submitAnswer("Skipped", currentStepObj.key)}>
          Skip for now
        </button>
      );
    }

    return null;
  };

  const renderSummary = () => {
    const summaryData = buildQuoteSummaryPayload({
      eventType: eventTypeLabel,
      quoteFormData: displayFormData || quoteFormData,
      answers,
    });

    return (
      <div className="cq-summary-wrap">
        <QuoteSummaryCard data={summaryData} />
        {onComplete && (
          <button
            className="cq-chip-done"
            style={{ marginTop: "16px", width: "100%" }}
            onClick={() => {
              if (onSubmit) {
                onSubmit(answers);
                return;
              }
              if (onComplete) onComplete();
            }}
          >
            Continue to Chat
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="cq-page-container">
      <div className="cq-page">
        {!activeService ? (
          <div className="cq-landing">
            <div className="cq-brand-bar">
              <div className="cq-brand-logo">V</div>
              <div>
                <div className="cq-brand-name">Veroa Studioz</div>
                <div className="cq-brand-tag">Photography & Videography</div>
              </div>
            </div>
            <div className="cq-hero">
              <h1>Tell us about<br/>your <em>occasion</em></h1>
              <p>Select your service and we will guide you through a few quick questions. Our team will have your personalised quote ready within 2 hours.</p>
            </div>
            {onComplete && (
               <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                 <button className="cq-chip-skip" onClick={onComplete}>Skip to Chat</button>
               </div>
            )}
            <div className="cq-divider-gold"><span>Choose your service</span></div>
            <div className="cq-svc-grid">
              {Object.keys(SERVICES).map(name => (
                <button key={name} className="cq-svc-btn" onClick={() => loadService(name)}>
                  <span className="cq-svc-icon">{SVC_ICONS[name] || '📷'}</span>{name}
                </button>
              ))}
            </div>
            <div className="cq-proto-badge">
              <div className="cq-proto-dot"></div>
              <span>Prototype — Veroa Studioz Client Flow v1.0</span>
            </div>
          </div>
        ) : (
          <div className="cq-chatbot">
            {!resolvedInitialService && (
              <button className="cq-back-btn" onClick={goBack}>Back to services</button>
            )}
            <div className="cq-chat-topbar">
              <div className="cq-chat-svc-name">
                {SVC_ICONS[matchedService] || '📷'} {eventTypeLabel || matchedService}
              </div>
              <div className="cq-chat-svc-sub">Personalised quote</div>
            </div>
            {displayFormData && (
              <div className="cq-form-summary">
                <div className="cq-fs-label">Already captured from your form</div>
                <div className="cq-fs-pills">
                  {displayFormData.name && <span className="cq-fs-pill">Name: {displayFormData.name}</span>}
                  {displayFormData.budget && <span className="cq-fs-pill">Budget: {displayFormData.budget}</span>}
                  {displayFormData.date && (
                    <span className="cq-fs-pill">Date: {displayFormData.date}</span>
                  )}
                  {displayFormData.eventSpan && (
                    <span className="cq-fs-pill">Event span: {displayFormData.eventSpan}</span>
                  )}
                  {displayFormData.city && <span className="cq-fs-pill">City: {displayFormData.city}</span>}
                  {displayFormData.coverage && <span className="cq-fs-pill">Coverage: {displayFormData.coverage}</span>}
                  {displayFormData.editing && <span className="cq-fs-pill">Editing: {displayFormData.editing}</span>}
                  {displayFormData.ceremonies && (
                    <span className="cq-fs-pill">Ceremonies: {displayFormData.ceremonies}</span>
                  )}
                  {displayFormData.addons && (
                    <span className="cq-fs-pill">Add-ons: {displayFormData.addons}</span>
                  )}
                </div>
              </div>
            )}
            <div className="cq-chat-shell">
              <div className="cq-progress-bar">
                <div className="cq-progress-fill" style={{ width: `${Math.round((currentStep / totalSteps) * 100)}%` }}></div>
              </div>
              <div className="cq-chat-head">
                <div className="cq-ch-avatar">V</div>
                <div>
                  <div className="cq-ch-name">Veroa Studioz</div>
                  <div className="cq-ch-status">Online now</div>
                </div>
              </div>
              <div className="cq-msgs">
                {msgs.map((m, i) => (
                  <div key={i} className={`cq-msg-row ${!m.isBot ? 'right' : ''}`}>
                    <div className={`cq-av ${m.isBot ? 'cq-av-bot' : 'cq-av-user'}`}>{m.isBot ? 'V' : 'You'}</div>
                    <div className={`cq-bub ${m.isBot ? 'cq-bub-bot' : 'cq-bub-user'}`}>{m.text}</div>
                  </div>
                ))}
                {isTyping && (
                  <div className="cq-msg-row">
                    <div className="cq-av cq-av-bot">V</div>
                    <div className="cq-typing-bub">
                      <div className="cq-td"></div><div className="cq-td"></div><div className="cq-td"></div>
                    </div>
                  </div>
                )}
                {currentStepObj?.final && !isTyping && renderSummary()}
                <div ref={msgsEndRef} />
              </div>
              <div className="cq-chips-area">
                {!currentStepObj?.final && renderChips()}
              </div>
              <div className="cq-input-row">
                <input 
                  className="cq-txt-input" 
                  placeholder="Type a message…" 
                  disabled={!currentStepObj?.freeText || isTyping}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyDown={(e) => { if(e.key === 'Enter' && inputValue.trim()) { submitAnswer(inputValue.trim(), currentStepObj.key); setInputValue(""); } }}
                />
                <button 
                  className="cq-send-btn" 
                  disabled={!currentStepObj?.freeText || isTyping || !inputValue.trim()}
                  onClick={() => { submitAnswer(inputValue.trim(), currentStepObj.key); setInputValue(""); }}
                >
                  <svg viewBox="0 0 24 24"><path d="M2 21L23 12 2 3v7l15 2-15 2v7z"/></svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationalQuote;
